export function TaxImplications() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Tax Implications and Strategies
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding tax implications is one of the most financially important tips for selling an apartment 
            building. Without proper planning, you could lose 30-40% of your profits to taxes. Learn strategies 
            to minimize tax liability and maximize your after-tax proceeds from the sale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/32e35031f8f20aaa14bd01ec1d8af87e.jpg"
              alt="Tax planning consultation"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Capital Gains Tax</h3>
            <p className="text-gray-600 mb-6">
              When you sell an apartment building for more than your adjusted cost basis, you'll owe capital 
              gains tax on the profit. The amount depends on how long you've owned the property and your income 
              tax bracket. Understanding these taxes is essential for planning your sale strategy.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3">Long-Term Capital Gains</h4>
                <p className="text-gray-600 text-sm mb-3">
                  Properties held longer than one year qualify for long-term capital gains rates, which are 
                  significantly lower than ordinary income rates. Current federal rates are 0%, 15%, or 20% 
                  depending on your income level.
                </p>
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-xs text-gray-700">
                    <strong>Example:</strong> If you're in the 20% capital gains bracket and sell for a $500,000 
                    profit, you'd owe approximately $100,000 in federal capital gains tax, plus state taxes.
                  </p>
                </div>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3">Depreciation Recapture</h4>
                <p className="text-gray-600 text-sm mb-3">
                  The IRS requires you to "recapture" depreciation deductions you've taken over the years. 
                  This recaptured depreciation is taxed at a maximum rate of 25%, which is higher than long-term 
                  capital gains rates.
                </p>
                <div className="bg-orange-50 p-3 rounded">
                  <p className="text-xs text-gray-700">
                    <strong>Important:</strong> Even if you didn't claim depreciation deductions, the IRS assumes 
                    you did and will still require recapture. Always claim depreciation to get the tax benefit.
                  </p>
                </div>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3">State and Local Taxes</h4>
                <p className="text-gray-600 text-sm">
                  Don't forget state and local capital gains taxes, which vary by location. Some states have 
                  no capital gains tax, while others tax gains as ordinary income at rates up to 13%. Factor 
                  these into your net proceeds calculations.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            1031 Exchange: Defer Capital Gains Tax
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            The 1031 exchange is one of the most powerful tax-deferral strategies and among the most valuable 
            tips for selling an apartment building. By reinvesting proceeds into another investment property, 
            you can defer all capital gains and depreciation recapture taxes indefinitely.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                1031 Exchange Requirements
              </h4>
              <ul className="text-gray-600 space-y-3 text-sm">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>
                  <span><strong>Like-Kind Property:</strong> Must exchange for another investment property 
                  (apartment buildings, commercial buildings, land, etc.)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>
                  <span><strong>45-Day Identification:</strong> Identify replacement properties within 45 days 
                  of selling your property</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>
                  <span><strong>180-Day Purchase:</strong> Complete purchase of replacement property within 
                  180 days of sale</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>
                  <span><strong>Equal or Greater Value:</strong> Replacement property must be equal or greater 
                  value to defer all taxes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>
                  <span><strong>Qualified Intermediary:</strong> Must use a qualified intermediary to hold 
                  proceeds during exchange</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>
                  <span><strong>No Receipt of Funds:</strong> Cannot receive sale proceeds directly or the 
                  exchange is disqualified</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-orange-600 mr-2"></i>
                1031 Exchange Strategies
              </h4>
              <div className="space-y-4">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Delayed Exchange (Most Common)</h5>
                  <p className="text-gray-600 text-xs">
                    Sell your property first, then identify and purchase replacement property within the 
                    required timeframes. This is the standard 1031 exchange structure.
                  </p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Reverse Exchange</h5>
                  <p className="text-gray-600 text-xs">
                    Purchase replacement property before selling your current property. Useful when you find 
                    the perfect replacement property but haven't sold yet.
                  </p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Improvement Exchange</h5>
                  <p className="text-gray-600 text-xs">
                    Use exchange funds to improve the replacement property before taking title. Allows you 
                    to add value while deferring taxes.
                  </p>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Delaware Statutory Trust (DST)</h5>
                  <p className="text-gray-600 text-xs">
                    Invest in fractional ownership of institutional-grade properties. Provides passive income 
                    and simplifies property management while completing your 1031 exchange.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <i className="ri-alert-line text-orange-600 mr-2"></i>
              Important 1031 Exchange Considerations
            </h4>
            <p className="text-gray-600 text-sm mb-3">
              While 1031 exchanges offer powerful tax benefits, they require careful planning and strict 
              adherence to IRS rules. Work with experienced qualified intermediaries and tax advisors to 
              ensure compliance and maximize benefits.
            </p>
            <ul className="text-gray-600 text-sm space-y-1">
              <li>• Start planning your exchange before listing your property</li>
              <li>• Identify multiple backup replacement properties in case deals fall through</li>
              <li>• Ensure your purchase contract allows for 1031 exchange assignment</li>
              <li>• Never receive or control sale proceeds during the exchange period</li>
              <li>• Consider market conditions and availability of suitable replacement properties</li>
            </ul>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Additional Tax Planning Strategies</h3>
            <p className="text-gray-600 mb-6">
              Beyond 1031 exchanges, several other strategies can help minimize taxes when selling apartment 
              buildings. These tips for selling an apartment building with tax efficiency can save significant 
              money and should be discussed with qualified tax professionals.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">Installment Sales</h4>
                <p className="text-gray-600 text-sm">
                  Structure the sale as an installment sale where you receive payments over multiple years. 
                  This spreads your capital gains tax liability across multiple tax years, potentially keeping 
                  you in lower tax brackets and reducing overall tax burden.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2">Opportunity Zone Investment</h4>
                <p className="text-gray-600 text-sm">
                  Invest capital gains into Qualified Opportunity Zone funds within 180 days of sale. This 
                  defers taxes until 2026 and can eliminate taxes on appreciation of the Opportunity Zone 
                  investment if held for 10+ years.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2">Cost Segregation Before Sale</h4>
                <p className="text-gray-600 text-sm">
                  Conduct cost segregation studies before selling to accelerate depreciation deductions. 
                  While this increases depreciation recapture, it can provide valuable deductions in high-income 
                  years before the sale.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-indigo-500">
                <h4 className="font-semibold text-gray-900 mb-2">Charitable Remainder Trust</h4>
                <p className="text-gray-600 text-sm">
                  Donate your property to a charitable remainder trust, which sells it tax-free and pays you 
                  income for life. You get immediate tax deductions, avoid capital gains tax, and support 
                  charitable causes while maintaining income.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=financial%20planning%20tax%20strategy%20meeting%20real%20estate%20investor%20with%20tax%20advisor%20reviewing%20documents%20calculating%20tax%20savings%20investment%20strategies%20professional%20consultation%20modern%20office&width=800&height=600&seq=tips-tax-002&orientation=landscape"
              alt="Tax strategy planning"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}